import React, { useMemo, useState } from 'react';
import { api } from '../api';
import Modal from './Modal';
import { PERM_GROUPS, PERMS } from '../perms';
import { ROLES } from '../datasheet';

const STATES = [['', 'Role default'], ['allow', 'Allow'], ['block', 'Block']];

// Per-person page access. Their role stays the baseline; each row can be left
// on the role default, explicitly allowed, or explicitly blocked.
export default function UserAccessModal({ user, onClose, onSaved, toast }) {
  const initial = useMemo(() => {
    const out = {};
    Object.entries(user.perm_overrides || {}).forEach(([perm, allow]) => { out[perm] = allow ? 'allow' : 'block'; });
    return out;
  }, [user]);
  const [state, setState] = useState(initial);
  const [saving, setSaving] = useState(false);

  const byRole = perm => (PERMS[perm] || []).includes(user.role);
  const effective = perm => (state[perm] === 'allow' ? true : state[perm] === 'block' ? false : byRole(perm));
  const changed = JSON.stringify(state) !== JSON.stringify(initial);
  const overrideCount = Object.values(state).filter(Boolean).length;

  async function save() {
    setSaving(true);
    try {
      const overrides = {};
      Object.entries(state).forEach(([perm, v]) => { if (v) overrides[perm] = v === 'allow'; });
      const updated = await api.setUserPerms(user.id, overrides);
      toast('Access saved for ' + (user.name || user.email) + ' — it applies next time they sign in');
      onSaved(updated);
      onClose();
    } catch (e) {
      toast(e?.response?.data?.detail || 'Could not save access', true);
    }
    setSaving(false);
  }

  return (
    <Modal open title={'Page access — ' + (user.name || user.email)} onClose={onClose} testid="user-access-modal">
      <p className="field-hint" data-testid="user-access-intro">
        Their role (<b>{ROLES[user.role] || user.role}</b>) is the baseline. Leave a row on
        <b> Role default</b> and it keeps following the role, so future role changes still apply.
        Changes take effect the next time they sign in.
      </p>
      <div className="access-grid" data-testid="user-access-grid">
        {PERM_GROUPS.map(([group, rows]) => (
          <div key={group} className="access-group">
            <div className="access-group-head">{group}</div>
            {rows.map(([perm, label]) => (
              <div key={perm} className="access-row" data-testid={'access-row-' + perm}>
                <div className="access-label">
                  {label}
                  <span className="access-default">{byRole(perm) ? 'role: allowed' : 'role: no access'}</span>
                </div>
                <select className="select access-select" data-testid={'access-select-' + perm}
                  value={state[perm] || ''}
                  onChange={e => setState(s => ({ ...s, [perm]: e.target.value }))}>
                  {STATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <span className={'access-effective ' + (effective(perm) ? 'yes' : 'no')} data-testid={'access-effective-' + perm}>
                  {effective(perm) ? 'Has access' : 'No access'}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 14 }}>
        <span className="field-hint" style={{ margin: 0, marginRight: 'auto' }} data-testid="user-access-count">
          {overrideCount ? overrideCount + ' setting' + (overrideCount !== 1 ? 's' : '') + ' overridden' : 'Following their role exactly'}
        </span>
        {overrideCount > 0 && (
          <button className="btn btn-sm" data-testid="user-access-reset" onClick={() => setState({})}>Reset all to role</button>
        )}
        <button className="btn" data-testid="user-access-cancel" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" data-testid="user-access-save" disabled={saving || !changed} onClick={save}>
          {saving ? 'Saving…' : 'Save access'}
        </button>
      </div>
    </Modal>
  );
}
